// src/utils/apiClient.js
import axios from "axios";
import { auth } from "../config/firebase";

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:8000",
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor for adding the auth token
apiClient.interceptors.request.use(
  async (config) => {
    // Get the current user
    const user = auth.currentUser;

    if (user) {
      try {
        // Get the ID token (forceRefresh = false)
        const token = await user.getIdToken();
        config.headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        console.error("Error getting auth token:", error);
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for handling common errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized errors (token expired)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const user = auth.currentUser;
        if (user) {
          // Force refresh token
          const token = await user.getIdToken(true);
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        // If refresh fails, redirect to login or handle error
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Utility methods for common API requests
const api = {
  get: (url, params) => apiClient.get(url, { params }),
  post: (url, data) => apiClient.post(url, data),
  put: (url, data) => apiClient.put(url, data),
  patch: (url, data) => apiClient.patch(url, data),
  delete: (url) => apiClient.delete(url),

  // Check URL accessibility
  checkUrlAccessibility: (url) => apiClient.post("/api/check-url-accessibility", { url }),

  user: {
    getProfile: () => apiClient.get("/api/user/profile"),
    updateProfile: (data) => apiClient.put("/api/user/profile", data),
  },

  dashboard: {
    getStats: () => apiClient.get("/dashboard/stats"),
    getActivities: () => apiClient.get("/dashboard/activities"),
  },

  projects: {
    /**
     * Create a new project + business details in one call.
     * @param {object} payload - { business_name: string, website: string, description: string, target_audiences: string[] }
     */
    create: (payload) => apiClient.post("/api/projects", payload),
    list: (config) => apiClient.get("/api/projects", config),
    get: (projectId, config) => apiClient.get(`/api/projects/${projectId}`, config),
    updateBusiness: (projectId, data) => apiClient.patch(`/api/projects/${projectId}/business`, data),
    setCompetitors: (projectId, urls) => apiClient.patch(`/api/projects/${projectId}/competitors`, { urls }),
    updateBloggingStyle: (projectId, data) => apiClient.patch(`/api/projects/${projectId}/blogging-style`, data),
    getBlogInfo: (id) => apiClient.get(`/api/projects/${id}/blog-info`),
    updateBlogInfo: (id, data) => apiClient.patch(`/api/projects/${id}/blog-info`, data),
    upsertRegionAndKeywords: (projectId, payload) => apiClient.patch(`/api/projects/${projectId}/region-keywords`, payload),
    /**
     * Detect links from a sitemap or blog root URL
     * @param {string} projectId - The project ID
     * @param {object} payload - { source: 'sitemap'|'blog', url: string }
     */
    detectLinks: (projectId, payload) => apiClient.post(`/api/projects/${projectId}/detect-links`, payload),
  },

  keywords: {
    /**
     * Get AI-generated keyword suggestions
     * @param {string} projectId - The project ID
     */
    getSuggestions: (projectId) => apiClient.get(`/api/projects/${projectId}/keyword-suggestions`),

    /**
     * Generate new keyword suggestions from DataForSEO
     * @param {string} projectId - The project ID
     */
    generateSuggestions: (projectId) => apiClient.post(`/api/projects/${projectId}/keyword-suggestions`),
  },

  generatedKeywords: {
    /**
     * Fetch keyword ideas already saved for a project.
     * @param {string} projectId
     * @param {object} params – optional { status, includeDeleted }
     */
    list: (projectId, params) => apiClient.get(`/api/projects/${projectId}/generated-keywords`, { params }),

    /**
     * Schedule keywords from keyword suggestions
     * @param {string} projectId - The project ID
     * @param {object} payload - { numKeywords: number }
     */
    add: (projectId, payload) => apiClient.post(`/api/projects/${projectId}/generated-keywords`, payload),

    /**
     * Directly add user-generated keywords to the calendar
     * @param {string} projectId - The project ID
     * @param {object} payload - { keyword: string, scheduled_for: string, user_generated: boolean }
     */
    directAdd: (projectId, payload) => apiClient.post(`/api/projects/${projectId}/direct-keywords`, payload),

    /**
     * Update fields such as difficulty, search_volume, status.
     */
    update: (keywordId, payload) => apiClient.patch(`/api/generated-keywords/${keywordId}`, payload),

    softDelete: keywordId => apiClient.delete(`/api/generated-keywords/${keywordId}`),
  },

  blogGeneration: {
    /**
     * Generate a blog post from a keyword
     * @param {object} payload - Contains keywordId and projectId
     */
    generate: (payload) => apiClient.post("/api/blog/generate", payload),

    /**
     * Get a blog post by keyword ID
     * @param {string} keywordId - The ID of the keyword
     */
    getBlogByKeyword: (keywordId) => apiClient.get(`/api/blog/${keywordId}`),

    /**
     * Update a blog post content
     * @param {string} keywordId - The ID of the keyword
     * @param {object} data - The updated content (title, meta_description, content, slug, status)
     * @returns {Promise} - API response
     */
    updateBlogContent: (keywordId, data) => {
      // Create the payload with the required fields
      const payload = {
        title: data.title,
        meta_description: data.meta_description,
        content: data.content,
        slug: data.slug
      };

      // Add status if provided
      if (data.status) {
        payload.status = data.status;
      }

      return apiClient.put(`/api/blog/${keywordId}`, payload, {
        timeout: 30000
      });
    },

    /**
     * Publish a generated blog by keyword ID via integration/webhook
     * @param {string} keywordId - The keyword ID linked to generated blog
     * @param {object} data - Publish options (integrationId or webhookUrl, publishStatus)
     */
    publishBlog: (keywordId, data) => {
      return apiClient.post(`/api/blog/${keywordId}/publish`, data, {
        timeout: 30000
      });
    },

    /**
     * Get content generated from competitor URL
     * @param {string} contentId - The ID of the generated content
     */
    getGeneratedContent: (contentId) => apiClient.get(`/api/generated-content/${contentId}`),

    /**
     * Update generated content
     * @param {string} contentId - The ID of the generated content
     * @param {object} data - The updated content (title, meta_description, content, slug)
     */
    updateGeneratedContent: (contentId, data) => {
      // Create the payload with the required fields
      const payload = {
        title: data.title,
        meta_description: data.meta_description,
        content: data.content,
        slug: data.slug
      };

      return apiClient.put(`/api/generated-content/${contentId}`, payload, {
        timeout: 30000
      });
    },

    /**
     * List all generated content from competitors for a project
     * @param {string} projectId - The ID of the project
     */
    listCompetitorContent: (projectId) => apiClient.get(`/api/projects/${projectId}/generated-content`)
  },

  websiteAnalysis: {
    /**
     * Analyze a website URL to extract business information
     * @param {string} url - The website URL to analyze
     * @returns {Promise} - API response with business information
     */
    analyze: (url) => apiClient.post("/api/website/analyze", { url }),
  },

  competitors: {
    getDetails: (competitorId) => apiClient.get(`/api/competitors/${competitorId}/details`),
    getSitemaps: (competitorId) => apiClient.get(`/api/competitors/${competitorId}/sitemaps`),
    addSitemap: (competitorId, url) => apiClient.post(`/api/competitors/${competitorId}/sitemaps`, { url }),
    deleteSitemap: (competitorId, sitemapId) => apiClient.delete(`/api/competitors/${competitorId}/sitemaps/${sitemapId}`),
    getBestPages: (competitorId) => apiClient.get(`/api/competitors/${competitorId}/best-pages`),
    addBestPage: (competitorId, url) => apiClient.post(`/api/competitors/${competitorId}/best-pages`, { url }),
    deleteBestPage: (competitorId, pageId) => apiClient.delete(`/api/competitors/${competitorId}/best-pages/${pageId}`),
    getUrls: (competitorId, page = 1, limit = 20, status, search) =>
      apiClient.get(`/api/competitors/${competitorId}/urls`, { params: { page, limit, status, search } }),
    addUrl: (competitorId, url) => apiClient.post(`/api/competitors/${competitorId}/urls`, { url }),
    updateUrlStatus: (urlId, status, contentId) =>
      apiClient.patch(`/api/competitor-urls/${urlId}/status`, { status, content_id: contentId }),
    autoFetchSitemaps: (competitorId) => apiClient.post(`/api/competitors/${competitorId}/auto-fetch-sitemaps`),
    generateContent: (urlId, projectId) => apiClient.post(`/api/competitor-urls/${urlId}/generate`, { projectId }),
    updateAutoTrack: (competitorId, enabled) => apiClient.patch(`/api/competitors/${competitorId}/auto-track`, { enabled }),
  },

  images: {
    /**
     * Process content with image placeholders and convert them to img tags
     * @param {string} content - The content with image placeholders
     * @returns {Promise} - API response with processed content
     */
    processContentImages: (content) => {
      return apiClient.post('/api/content/process-images', { content });
    }
  }
};

export default api;
